/**
 * 常用工具方法集合
 * 包含时间处理、树形数据转换、设备检测、坐标转换、深拷贝等通用工具
 */
export default {
  /**
   * 倒计时 - 每秒回调一次，直到结束时间
   * @param {string|number} endTime - 结束时间（时间戳或日期字符串）
   * @param {number|Function} serverTime - 服务端当前时间戳，或直接传入回调函数
   * @param {Function} [callback] - 回调函数，参数为 [天, 时, 分, 秒]、服务端时间、定时器实例
   * @returns {number} 定时器实例（可用于 clearTimeout 取消倒计时）
   */
  countdown(endTime, serverTime, callback) {
    let type = typeof serverTime === 'function',
      end = new Date(endTime).getTime(),
      now = new Date(!serverTime || type ? new Date().getTime() : serverTime).getTime(),
      count = end - now,
      time = [
        Math.floor(count / (1000 * 60 * 60 * 24)), // 天
        Math.floor(count / (1000 * 60 * 60)) % 24, // 时
        Math.floor(count / (1000 * 60)) % 60, // 分
        Math.floor(count / 1000) % 60, // 秒
      ];
    // 如果第二个参数直接传的函数，则修正参数赋值
    if (type) callback = serverTime;
    let timer = setTimeout(() => {
      this.countdown(endTime, now + 1000, callback);
    }, 1000);
    callback && callback(count > 0 ? time : [0, 0, 0, 0], serverTime, timer);
    // 倒计时结束后清除定时器
    if (count <= 0) clearTimeout(timer);
    return timer;
  },

  /**
   * 语义化时间 - 将时间转换为"x分钟前"、"x小时前"等友好格式
   * @param {string|number|Date} time - 需要语义化的时间
   * @param {boolean} [onlyDate] - 超过30天时是否仅返回日期部分（不含时间）
   * @returns {string} 语义化后的时间字符串
   */
  timeAgo(time, onlyDate) {
    if (!time) return '';
    // 兼容 iOS 日期格式（将 '-' 替换为 '/'）
    if (typeof time === 'string') time = time.replace(/-/g, '/');
    let arr = [[], []],
      stamp = new Date().getTime() - new Date(time).getTime();
    // 超过31天返回具体日期格式
    if (stamp > 1000 * 60 * 60 * 24 * 31) {
      stamp = new Date(time);
      arr[0][0] = this.digit(stamp.getFullYear(), 4);
      arr[0][1] = this.digit(stamp.getMonth() + 1);
      arr[0][2] = this.digit(stamp.getDate());
      if (!onlyDate) {
        arr[1][0] = this.digit(stamp.getHours());
        arr[1][1] = this.digit(stamp.getMinutes());
        arr[1][2] = this.digit(stamp.getSeconds());
      }
      return arr[0].join('-') + ' ' + arr[1].join(':');
    }
    // 30天以内返回语义化时间
    if (stamp >= 1000 * 60 * 60 * 24) {
      return ((stamp / 1000 / 60 / 60 / 24) | 0) + '天前';
    } else if (stamp >= 1000 * 60 * 60) {
      return ((stamp / 1000 / 60 / 60) | 0) + '小时前';
    } else if (stamp >= 1000 * 60 * 3) {
      return ((stamp / 1000 / 60) | 0) + '分钟前';
    } else if (stamp < 0) {
      return '未来';
    } else {
      return '刚刚'; // 3分钟以内显示"刚刚"
    }
  },

  /**
   * 数字前置补零 - 将数字格式化为指定长度的字符串，不足部分前置补0
   * @param {number|string} num - 需要补零的数字
   * @param {number} [length=2] - 目标位数，默认2位
   * @returns {string} 补零后的字符串
   */
  digit(num, length) {
    let str = '';
    num = String(num);
    length = length || 2;
    for (let i = num.length; i < length; i++) str += '0';
    return num < Math.pow(10, length) ? str + (num | 0) : num;
  },

  /**
   * 时间戳转日期字符串 - 将时间戳或Date对象格式化为指定格式的日期字符串
   * @param {number|string|Date} time - 时间戳、日期字符串或Date对象
   * @param {string} [format='yyyy-MM-dd HH:mm:ss'] - 日期格式模板
   * @returns {string} 格式化后的日期字符串
   */
  toDateString(time, format) {
    if (!time) return '';
    if (typeof time === 'string') time = time.replace(/-/g, '/');
    let date = new Date(time || new Date()),
      ymd = [this.digit(date.getFullYear(), 4), this.digit(date.getMonth() + 1), this.digit(date.getDate())],
      hms = [this.digit(date.getHours()), this.digit(date.getMinutes()), this.digit(date.getSeconds())];
    format = format || 'yyyy-MM-dd HH:mm:ss';
    return format
      .replace(/yyyy/g, ymd[0])
      .replace(/MM/g, ymd[1])
      .replace(/dd/g, ymd[2])
      .replace(/HH/g, hms[0])
      .replace(/mm/g, hms[1])
      .replace(/ss/g, hms[2]);
  },

  /**
   * HTML转义 - 将特殊字符转义为HTML实体，防止XSS攻击
   * @param {string} html - 需要转义的原始字符串
   * @returns {string} 转义后的安全字符串
   */
  escape(html) {
    return String(html || '')
      .replace(/&(?!#?[a-zA-Z0-9]+;)/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/'/g, '&#39;')
      .replace(/"/g, '&quot;');
  },

  /**
   * 扁平数据转树形结构 - 将 pid 形式的扁平数组转换为 children 嵌套的树形数据
   * @param {Array} data - 扁平数据数组
   * @param {string} idKey - id 字段名称
   * @param {string} pidKey - 父级 id 字段名称
   * @param {string} [childKey='children'] - 生成的子节点字段名称
   * @param {string|number|Array} [pid] - 顶级节点的 pid 值，不传则自动检测
   * @returns {Array} 树形结构数组
   */
  toTreeData(data, idKey, pidKey, childKey, pid) {
    if (!childKey) childKey = 'children';
    if (pid === undefined) {
      // 自动查找顶级 pid（不存在于任何节点 id 中的 pid）
      pid = [];
      data.forEach(d => {
        let flag = true;
        for (let i = 0; i < data.length; i++) {
          if (d[pidKey] === data[i][idKey]) {
            flag = false;
            break;
          }
        }
        if (flag) pid.push(d[pidKey]);
      });
    }
    let result = [];
    data.forEach(d => {
      // 防止 id 和 pid 相同导致无限递归
      if (d[idKey] === d[pidKey]) return console.error('data error: ', d);
      if (Array.isArray(pid) ? pid.indexOf(d[pidKey]) !== -1 : d[pidKey] === pid) {
        let children = this.toTreeData(data, idKey, pidKey, childKey, d[idKey]);
        if (children.length > 0) d[childKey] = children;
        result.push(d);
      }
    });
    return result;
  },

  /**
   * 递归遍历树形数据 - 深度优先遍历 children 形式的树形结构
   * @param {Array} data - 树形数据数组
   * @param {Function} callback - 每个节点的回调函数，返回 false 可阻止遍历子节点
   * @param {string} [childKey='children'] - 子节点字段名称
   */
  eachTreeData(data, callback, childKey) {
    if (!childKey) childKey = 'children';
    data.forEach(d => {
      if (callback(d) !== false && d[childKey]) this.eachTreeData(d[childKey], callback, childKey);
    });
  },

  /**
   * 浏览器全屏切换 - 在当前全屏/非全屏状态之间切换
   * @returns {boolean|Element} 切换前的全屏状态（true 表示之前是全屏）
   */
  fullScreen() {
    let isFull = document.fullscreenElement || document.msFullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || false;
    if (isFull) {
      // 退出全屏
      let efs = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
      if (efs) {
        efs.call(document);
      }
    } else {
      // 进入全屏
      let el = document.documentElement;
      let rfs = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
      if (rfs) {
        rfs.call(el);
      }
    }
    return isFull;
  },

  /**
   * 获取屏幕可视区域宽度
   * @returns {number} 屏幕宽度（px）
   */
  screenWidth() {
    return document.documentElement.clientWidth || document.body.clientWidth;
  },

  /**
   * 获取屏幕可视区域高度
   * @returns {number} 屏幕高度（px）
   */
  screenHeight() {
    return document.documentElement.clientHeight || document.body.clientHeight;
  },

  /**
   * HTML转纯文本 - 去除所有 HTML 标签，返回纯文本内容
   * @param {string} html - 包含 HTML 标签的字符串
   * @returns {string} 去除标签后的纯文本
   */
  htmlToText(html) {
    return html.replace(/<[^>]+>/g, '');
  },

  /**
   * 获取设备信息 - 检测当前浏览器所在的操作系统、是否微信环境等
   * @param {string} [key] - 自定义 UserAgent 关键字，用于检测特定应用版本
   * @returns {Object} 设备信息对象 { os, ie, weixin, android, ios, mobile }
   */
  device(key) {
    let agent = navigator.userAgent.toLowerCase(),
      /**
       * 从 UserAgent 中提取指定应用的版本号
       * @param {string} label - 应用标识名
       * @returns {string|false} 版本号或 false
       */
      getVersion = function(label) {
        const exp = new RegExp(label + '/([^\\s\\_\\-]+)');
        label = (agent.match(exp) || [])[1];
        return label || false;
      },
      result = {
        /** 底层操作系统类型：windows / linux / ios / mac / android */
        os: (function() {
          if (/windows/.test(agent)) {
            return 'windows';
          } else if (/linux/.test(agent)) {
            return 'linux';
          } else if (/iphone|ipod|ipad|ios/.test(agent)) {
            return 'ios';
          } else if (/mac/.test(agent)) {
            return 'mac';
          } else if (/android/.test(agent)) {
            return 'android';
          }
        })(),
        /** IE浏览器版本号，非IE返回 false */
        ie: (function() {
          return !!window.ActiveXObject || 'ActiveXObject' in window ? (agent.match(/msie\s(\d+)/) || [])[1] || '11' : false;
        })(),
        /** 微信版本号，非微信环境返回 false */
        weixin: getVersion('micromessenger'),
      };
    // 支持自定义关键字检测
    if (key && !result[key]) {
      result[key] = getVersion(key);
    }
    // 移动端标识
    result.android = /android/.test(agent);
    result.ios = result.os === 'ios';
    result.mobile = !!(result.android || result.ios);
    return result;
  },

  /**
   * 生成随机字符串ID - 由大小写字母和数字组成
   * @param {number} [length=32] - 生成的字符串长度
   * @returns {string} 随机字符串
   */
  uuid(length = 32) {
    const num = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890';
    let str = '';
    for (let i = 0; i < length; i++) {
      str += num.charAt(Math.floor(Math.random() * num.length));
    }
    return str;
  },

  /**
   * 生成指定范围的随机整数 - 生成 m 到 n 之间的随机数（不包含 n）
   * @param {number} m - 范围起始值
   * @param {number} n - 范围结束值（不包含）
   * @returns {number} 随机整数
   */
  random(m, n) {
    return Math.floor(Math.random() * (m - n) + n);
  },

  /**
   * 百度地图坐标转高德地图坐标（BD-09 → GCJ-02）
   * @param {Object} point - 百度坐标对象 { lng, lat }
   * @returns {Object} 高德坐标对象 { lng, lat }
   */
  bd09ToGcj02(point) {
    const x_pi = (3.14159265358979324 * 3000.0) / 180.0;
    const x = point.lng - 0.0065,
      y = point.lat - 0.006;
    const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * x_pi);
    const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * x_pi);
    return { lng: z * Math.cos(theta), lat: z * Math.sin(theta) };
  },

  /**
   * 高德地图坐标转百度地图坐标（GCJ-02 → BD-09）
   * @param {Object} point - 高德坐标对象 { lng, lat }
   * @returns {Object} 百度坐标对象 { lng, lat }
   */
  gcj02ToBd09(point) {
    const x_pi = (3.14159265358979324 * 3000.0) / 180.0;
    const x = point.lng,
      y = point.lat;
    const z = Math.sqrt(x * x + y * y) + 0.00002 * Math.sin(y * x_pi);
    const theta = Math.atan2(y, x) + 0.000003 * Math.cos(x * x_pi);
    return {
      lng: z * Math.cos(theta) + 0.0065,
      lat: z * Math.sin(theta) + 0.006,
    };
  },

  /**
   * 深度克隆对象 - 递归复制 Object 和 Array 类型的所有属性
   * 注意：不支持 Date、RegExp、Map、Set 等特殊类型及循环引用
   * @param {Object|Array} obj - 需要克隆的对象
   * @returns {Object|Array} 克隆后的新对象
   */
  deepClone(obj) {
    let result;
    const type = this.typeOf(obj);
    if (type === 'Object') result = {};
    else if (type === 'Array') result = [];
    else return obj;
    Object.keys(obj).forEach(key => {
      const copy = obj[key],
        cType = this.typeOf(copy);
      if (cType === 'Object' || cType === 'Array') result[key] = this.deepClone(copy);
      else result[key] = obj[key];
    });
    return result;
  },

  /**
   * 获取变量精确类型 - 基于 Object.prototype.toString 获取准确的类型名
   * @param {*} obj - 任意变量
   * @returns {string} 类型名称，如 'Object'、'Array'、'String'、'Null'、'Undefined' 等
   */
  typeOf(obj) {
    if (obj === null) return 'Null';
    if (obj === undefined) return 'Undefined';
    return Object.prototype.toString.call(obj).slice(8, -1);
  },

  /**
   * 播放音频文件
   * @param {string} url - 音频文件的 URL 地址
   * @returns {Promise} 播放 Promise
   */
  play(url) {
    return new Audio(url).play();
  },

  /**
   * 判断富文本内容是否为空 - 检测 HTML 中是否包含有效文本或媒体元素
   * @param {string} html - 富文本 HTML 字符串
   * @returns {boolean} true 表示内容为空
   */
  htmlIsBlank(html) {
    if (!html) return true;
    // 包含媒体标签则视为非空
    const media = ['img', 'audio', 'video', 'iframe', 'object'];
    for (let i = 0; i < media.length; i++) {
      if (html.indexOf('<' + media[i]) > -1) return false;
    }
    let str = html.replace(/\s*/g, '');
    if (!str) return true;
    str = str.replace(/&nbsp;/gi, '');
    if (!str) return true;
    str = str.replace(/<[^>]+>/g, '');
    return !str;
  },

  /**
   * 导出 Excel 文件 - 使用 XLSX 库将数据导出为 Excel
   * @param {Object} XLSX - XLSX 库对象
   * @param {Array|Object} sheet - 二维数组数据或已创建的 sheet 对象
   * @param {string} [sheetname='sheet1'] - 导出的文件名（不含后缀）
   * @param {string} [type='xlsx'] - 文件格式（xlsx / csv 等）
   */
  exportSheet(XLSX, sheet, sheetname, type) {
    if (!sheetname) sheetname = 'sheet1';
    if (!type) type = 'xlsx';
    if (Array.isArray(sheet)) sheet = XLSX.utils.aoa_to_sheet(sheet);
    let workbook = { SheetNames: [sheetname], Sheets: {} };
    workbook.Sheets[sheetname] = sheet;
    XLSX.writeFile(workbook, sheetname + '.' + type);
  },

  /**
   * 手机号脱敏 - 将手机号中间4位替换为星号（如 138****1234）
   * @param {string} phone - 11位手机号
   * @returns {string} 脱敏后的手机号，传入空值返回空字符串
   */
  phoneSafe(phone) {
    if (!phone) return '';
    return String(phone).substr(0, 3) + '****' + String(phone).substr(7);
  },

  /**
   * 递归查找树节点ID - 根据指定展开层级，收集需要展开的节点 key 数组
   * 用于 Element UI Tree 组件的 default-expanded-keys
   * @param {Array} child - 当前层级的子节点数组
   * @param {number} degree - 当前迭代层级（从1开始）
   * @param {number} developFloor - 需要展开到的目标层级
   * @param {Array} dataIdArr - 存储展开节点 key 的数组
   * @param {string} [dataId='dataId'] - 树节点 key 绑定的字段名
   * @returns {Array} 展开节点的 key 数组
   */
  findChild(child, degree, developFloor, dataIdArr, dataId) {
    dataId = dataId || 'dataId';
    if (degree === developFloor) {
      // 到达目标层级，收集当前层所有节点 ID
      for (let i = 0; i < child.length; i++) {
        dataIdArr.push(child[i][dataId]);
      }
    } else {
      // 未到达目标层级，递归遍历子节点
      if (child && child.length > 0) {
        let index = degree + 1;
        for (let i = 0; i < child.length; i++) {
          if (child[i].children) {
            this.findChild(child[i].children, index, developFloor, dataIdArr, dataId);
          }
        }
      }
    }
    return dataIdArr;
  },

  /**
   * 获取树形展开层级数据 - 计算需要默认展开的节点 key 数组
   * 配合 findChild 方法使用，用于树组件初始化展开状态
   * @param {Array} tree - 完整的树形数据
   * @param {number} developFloor - 需要展开的层级数
   * @param {Array} [dataIdArr] - 预置的展开节点数组（会被重置）
   * @param {string} [dataId='dataId'] - 树节点 key 绑定的字段名
   * @returns {Array|undefined} 展开节点的 key 数组，空树返回 undefined
   */
  getTreeFloor(tree, developFloor, dataIdArr, dataId) {
    if (tree.length <= 0) {
      return;
    }
    dataIdArr = [];
    dataId = dataId || 'dataId';
    dataIdArr = this.findChild(tree, 1, developFloor, dataIdArr, dataId);
    return dataIdArr;
  },

  /**
   * 图表常用配色数组 - 提供24种预设颜色用于 ECharts 等图表的系列配色
   */
  beautifulColors: [
    'rgb(24,144,255)',
    'rgb(102,181,255)',
    'rgb(65, 217, 199)',
    'rgb(47, 194, 91)',
    'rgb(110, 219, 143)',
    'rgb(154, 230, 92)',
    'rgb(250, 204, 20)',
    'rgb(230, 150, 92)',
    'rgb(87, 173, 113)',
    'rgb(34, 50, 115)',
    'rgb(115, 138, 230)',
    'rgb(117, 100, 204)',
    'rgb(133, 67, 224)',
    'rgb(168, 119, 237)',
    'rgb(92, 142, 230)',
    'rgb(19, 194, 194)',
    'rgb(112, 224, 224)',
    'rgb(92, 163, 230)',
    'rgb(52, 54, 199)',
    'rgb(128, 130, 255)',
    'rgb(221, 129, 230)',
    'rgb(240, 72, 100)',
    'rgb(250, 125, 146)',
    'rgb(213, 152, 217)',
  ],
};
